import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Drawer, List, ListItemText, Typography, ListItemButton, Accordion, AccordionSummary, AccordionDetails, IconButton, useTheme, useMediaQuery } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MenuIcon from '@mui/icons-material/Menu';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import PersonIcon from '@mui/icons-material/Person';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AppsIcon from '@mui/icons-material/Apps';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SourceIcon from '@mui/icons-material/Source';
import AddCardIcon from '@mui/icons-material/AddCard';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BuildIcon from '@mui/icons-material/Build';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import AlarmIcon from '@mui/icons-material/Alarm';
import PaidIcon from '@mui/icons-material/Paid';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PrintIcon from '@mui/icons-material/Print';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import userIcon from '../../assets/userIcon.svg';

const PinkMenu: React.FC = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const menuItems = [
    {
      subHeading: 'Adminisztráció',
      items: [
        { label: 'Cég', path: '/company-registration', icon: <BusinessIcon /> },
        { label: 'Üzleti egységek', path: '/business-units', icon: <AccountTreeIcon /> },
        { label: 'Felhasználók', path: '/users', icon: <PersonIcon /> },
        { label: 'Csoportok kezelése', path: '/groups', icon: <SettingsIcon /> },
      ],
    },
    {
      subHeading: 'Árlap',
      items: [
        { label: 'Termékek', path: '/products', icon: <StorefrontIcon /> },
        { label: 'Extrák', path: '/extras', icon: <FastfoodIcon /> },
        { label: 'Árlapok kezelése', path: '/price-management', icon: <MenuBookIcon /> },
        { label: 'Gyors menü', path: '/quick-menu', icon: <PointOfSaleIcon /> },
      ],
    },
    {
      subHeading: 'Eszközök kezelése',
      items: [
        { label: 'Terminálok', path: '/terminals', icon: <AppsIcon /> },
        { label: 'Konyhai nyomtatók', path: '/kitchen-printers', icon: <PrintIcon /> },
      ],
    },
    {
      subHeading: 'Vezérlőpult',
      items: [
        { label: 'Vezérlőpult', path: '/dashboard', icon: <DashboardIcon /> },
        { label: 'Kimutatások', path: '/reports', icon: <AssessmentIcon /> },
      ],
    },
    {
      subHeading: 'Raktár',
      items: [
        { label: 'Raktár tételek', path: '/warehouse-items', icon: <WarehouseIcon /> },
        { label: 'Raktárak kezelése', path: '/warehouse-management', icon: <BuildIcon /> },
        { label: 'Szállítók', path: '/suppliers', icon: <LocalShippingIcon /> },
        { label: 'Raktár műveletek', path: '/warehouse-operations', icon: <ChecklistRtlIcon /> },
        { label: 'Leltár', path: '/inventory', icon: <ListAltIcon /> },
        { label: 'Készletváltozások', path: '/stock-changes', icon: <CreditScoreIcon /> },
        { label: 'Raktárkészlet', path: '/warehouse-stock', icon: <AlarmIcon /> },
      ],
    },
    {
      subHeading: 'Vásárlás',
      items: [
        { label: 'Licensz vásárlás', path: '/license-purchase', icon: <PaidIcon /> },
        { label: 'Eszközök vásárlása', path: '/equipment-purchase', icon: <ShoppingCartIcon /> },
      ],
    },
    {
      subHeading: 'Felhasználói kézikönyv',
      items: [
        { label: 'Admin', path: '/admin-manual', icon: <MenuBookIcon /> },
        { label: 'App', path: '/app-manual', icon: <MenuBookIcon /> },
      ],
    },
    {
      subHeading: 'Corporate Admin',
      items: [
        { label: 'Licensz vásárlás', path: '/corporate-license-purchase', icon: <PaidIcon /> },
        { label: 'Vásárlási előzmények', path: '/purchase-history', icon: <CardGiftcardIcon /> },
        { label: 'Műszakzárás', path: '/shift-closure', icon: <AlarmIcon /> },
        { label: 'Pénzügy', path: '/finance', icon: <CreditScoreIcon /> },
        { label: 'Voucher generálás', path: '/voucher-generation', icon: <CardGiftcardIcon /> },
      ],
    },
  ];

  const handleMenuClick = (path: string, label: string, external?: boolean) => {
    setSelectedItem(label);
    setIsDrawerOpen(false);
    if (external) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
  };

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <>
      {isSmallScreen ? (
        <>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setIsDrawerOpen(true)}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 1100
            }}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            variant="temporary"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 270 } }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ padding: 2 }}>
                <Box sx={{ marginRight: 1, width: 24, height: 24, borderRadius: '50%', backgroundColor: '#FFD8E9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={userIcon} alt="user icon" style={{ width: '16px', height: '16px' }} />
                </Box>
                <Typography variant="body1">user@easdasdasdxample.com</Typography>
                kaka
              </Box>
              <hr style={{ width: '85%', opacity: 0.3 }} />
              {menuItems.map((section) => (
                <Accordion
                  key={section.subHeading}
                  expanded={expanded === section.subHeading}
                  onChange={handleChange(section.subHeading)}
                  sx={{ backgroundColor: 'transparent', boxShadow: 'none', '&::before': { display: 'none' } }}
                >
                  <AccordionSummary
                    expandIcon={<ArrowDropDownIcon />}
                    aria-controls={`${section.subHeading}-content`}
                    id={`${section.subHeading}-header`}
                  >
                    <Typography variant="subtitle1">{section.subHeading}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {section.items.map((item) => (
                        <ListItemButton
                          key={item.label}
                          onClick={() => handleMenuClick(item.path, item.label)}
                          sx={{
                            backgroundColor: selectedItem === item.label ? '#FFD8E9' : 'transparent',
                            borderRadius: '30px',
                            '&:hover': { backgroundColor: '#FFD8E9' },
                          }}
                        >
                          {item.icon}
                          <ListItemText primary={item.label} sx={{ marginLeft: 1 }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Drawer>
        </>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: 270,
            flexShrink: 0,
            backgroundColor: '#F9EAEF',
            [`& .MuiDrawer-paper`]: { width: 270, boxSizing: 'border-box', overflowX: 'hidden' },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', padding: 2 }}>
              <Box sx={{ marginRight: 1, width: 24, height: 24, borderRadius: '50%', backgroundColor: '#FFD8E9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={userIcon} alt="user icon" style={{ width: '16px', height: '16px' }} />
              </Box>
              <Typography variant="body1">user@exampdsfsdfle.com</Typography>
              <SettingsIcon sx={{cursor: 'pointer'}}/>
            </Box>
            <hr style={{ width: '85%', opacity: 0.3 }} />
            {menuItems.map((section) => (
              <Accordion
                key={section.subHeading}
                expanded={expanded === section.subHeading}
                onChange={handleChange(section.subHeading)}
                sx={{ backgroundColor: 'transparent', boxShadow: 'none', '&::before': { display: 'none' } }}
              >
                <AccordionSummary
                  expandIcon={<ArrowDropDownIcon />}
                  aria-controls={`${section.subHeading}-content`}
                  id={`${section.subHeading}-header`}
                >
                  <Typography variant="subtitle1">{section.subHeading}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {section.items.map((item) => (
                      <ListItemButton
                        key={item.label}
                        onClick={() => handleMenuClick(item.path, item.label)}
                        sx={{
                          backgroundColor: selectedItem === item.label ? '#FFD8E9' : 'transparent',
                          borderRadius: '30px',
                          '&:hover': { backgroundColor: '#FFD8E9' },
                        }}
                      >
                        {item.icon}
                        <ListItemText primary={item.label} sx={{ marginLeft: 1 }} />
                      </ListItemButton>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default PinkMenu;